// FigJam-style widgets, shared by the app (sandboxed iframe) and the server (agents):
// the runtime that renders widget code, the mapping of rendered elements to layers, and
// applying a render to the document.

import type { Editor } from './editor.ts';
import { createFromSpec, specToProps, type NodeSpec } from './spec.ts';
import type { NodeId } from './types.ts';

/** Inside the sandbox: a tiny widget runtime. Receives {state, action} and returns {tree, state, menu}. */
export const WIDGET_RUNTIME = String.raw`
let __component = null; let __state = {}; let __menu = null; let __menuHandler = null; const __handlers = new Map(); const __inputs = new Map(); let __effects = []; let __tasks = [];
const h = (type, props, ...children) => ({ type, props: props || {}, children: children.flat(Infinity).filter((c) => c !== null && c !== undefined && c !== false) });
const mk = (name) => (props) => ({ type: name, props: props || {}, children: [] });
const widget = {
  h, register: (fn) => { __component = fn; },
  AutoLayout: 'AutoLayout', Frame: 'Frame', Text: 'Text', Rectangle: 'Rectangle', Ellipse: 'Ellipse', SVG: 'SVG', Image: 'Image', Line: 'Line', Span: 'Span', Input: 'Input', Fragment: 'Fragment',
  useSyncedState: (key, init) => { if (!(key in __state)) __state[key] = typeof init === 'function' ? init() : init; return [__state[key], (v) => { __state[key] = typeof v === 'function' ? v(__state[key]) : v; }]; },
  useSyncedMap: (key) => { const m = (__state['map:' + key] ||= {}); return { get: (k) => m[k], set: (k, v) => { m[k] = v; }, delete: (k) => { delete m[k]; }, keys: () => Object.keys(m), values: () => Object.values(m), entries: () => Object.entries(m), get size() { return Object.keys(m).length; } }; },
  usePropertyMenu: (items, handler) => { __menu = items.filter((i) => i.itemType === 'action' || !i.itemType).map((i) => ({ propertyName: i.propertyName, tooltip: i.tooltip || i.propertyName })); __menuHandler = handler; },
  useEffect: (fn) => { __effects.push(fn); }, waitForTask: (p) => { __tasks.push(Promise.resolve(p)); }, useWidgetNodeId: () => 'widget',
};
const figma = { widget, notify: (m) => (typeof __notify === 'function' ? __notify(String(m)) : undefined) };
function render() {
  __handlers.clear(); __inputs.clear(); __menu = null; __effects = [];
  const walk = (el, path) => {
    if (typeof el === 'string' || typeof el === 'number') return { type: 'Text', props: {}, children: [String(el)], path };
    if (typeof el.type === 'function') return walk(el.type({ ...el.props, children: el.children }), path);
    if (el.type === 'Fragment') return { type: 'AutoLayout', props: { direction: 'vertical', spacing: 0 }, children: el.children.map((c, i) => walk(c, path + '.' + i)), path };
    if (el.props.onClick) __handlers.set(path, el.props.onClick);
    if (el.type === 'Input') __inputs.set(path, el.props.onTextEditEnd);
    const kids = el.type === 'Text' || el.type === 'Span' ? el.children.map(String) : el.type === 'Input' ? [] : el.children.map((c, i) => walk(c, path + '.' + i));
    const { onClick, onTextEditEnd, ...props } = el.props;
    return { type: el.type, props, children: kids, path, clickable: !!onClick, input: el.type === 'Input' };
  };
  return walk(h(__component, {}), '0');
}
async function __run(state, action) {
    __state = state || {};
    const m = { action };
    render();
    if (m.action && m.action.kind === 'click' && __handlers.has(m.action.path)) await __handlers.get(m.action.path)({});
    if (m.action && m.action.kind === 'menu' && __menuHandler) await __menuHandler({ propertyName: m.action.propertyName });
    if (m.action && m.action.kind === 'input' && __inputs.get(m.action.path)) await __inputs.get(m.action.path)({ characters: String(m.action.characters) });
    for (const fn of __effects) { try { fn(); } catch (e) {} }
    // tasks started by handlers or effects finish before the widget re-renders
    while (__tasks.length) await Promise.all(__tasks.splice(0));
    const tree = render();
    return { tree, state: __state, menu: __menu };
}
`;

export interface WidgetElement {
  type: string;
  props: Record<string, unknown>;
  children: (WidgetElement | string)[];
  path: string;
  clickable?: boolean;
  input?: boolean;
}

const color = (v: unknown) => (typeof v === 'string' ? v : undefined);
const size = (v: unknown) => (v === 'hug-contents' ? 'hug' : v === 'fill-parent' ? 'fill' : typeof v === 'number' ? v : undefined);
const pad = (v: unknown) => {
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object') {
    const p = v as { vertical?: number; horizontal?: number; top?: number; right?: number; bottom?: number; left?: number };
    return [p.top ?? p.vertical ?? 0, p.right ?? p.horizontal ?? 0, p.bottom ?? p.vertical ?? 0, p.left ?? p.horizontal ?? 0] as [number, number, number, number];
  }
  return undefined;
};
const ALIGN: Record<string, string> = { start: 'start', center: 'center', end: 'end' };

/** A rendered widget element → the spec format (with its path as the ref). */
export function widgetToSpec(el: WidgetElement): NodeSpec {
  const p = el.props;
  const common = { ref: el.path, name: (p.name as string) ?? el.type, opacity: p.opacity as number | undefined } as Partial<NodeSpec>;
  const stroke = color(p.stroke) ? { stroke: color(p.stroke), strokeWidth: (p.strokeWidth as number) ?? 1 } : {};
  const radius = typeof p.cornerRadius === 'number' ? { radius: p.cornerRadius } : {};
  const effects = p.effect ? { shadow: true } : {};
  const w = size(p.width),
    hgt = size(p.height);
  switch (el.type) {
    case 'AutoLayout': {
      const vertical = p.direction === 'vertical';
      const main = vertical ? p.verticalAlignItems : p.horizontalAlignItems;
      const cross = vertical ? p.horizontalAlignItems : p.verticalAlignItems;
      return {
        ...common, type: 'frame', layout: vertical ? 'column' : 'row', gap: (p.spacing as number) ?? 0, padding: pad(p.padding),
        fill: color(p.fill) ?? null, ...stroke, ...radius, ...effects,
        width: w ?? 'hug', height: hgt ?? 'hug',
        ...(main ? { align: ALIGN[main as string] ?? 'start' } : {}), ...(cross ? { crossAlign: ALIGN[cross as string] ?? 'start' } : {}),
        children: (el.children as WidgetElement[]).map(widgetToSpec),
      } as NodeSpec;
    }
    case 'Frame':
      return { ...common, type: 'frame', width: (w as number) ?? 100, height: (hgt as number) ?? 100, fill: color(p.fill) ?? null, ...stroke, ...radius, ...effects, children: (el.children as WidgetElement[]).map(widgetToSpec) } as NodeSpec;
    case 'Text':
    case 'Span':
      return {
        ...common, type: 'text', text: (el.children as string[]).join(''), fontSize: (p.fontSize as number) ?? 16, fontWeight: (p.fontWeight as number) ?? undefined,
        fill: color(p.fill) ?? '#000000', ...(p.horizontalAlignText ? { textAlign: p.horizontalAlignText } : {}), ...(typeof w === 'number' ? { width: w } : w === 'fill' ? { width: 'fill' } : {}),
      } as NodeSpec;
    case 'Rectangle':
    case 'Ellipse':
      return { ...common, type: el.type === 'Rectangle' ? 'rect' : 'ellipse', width: (w as number) ?? 100, height: (hgt as number) ?? 100, fill: color(p.fill) ?? '#d9d9d9', ...stroke, ...radius } as NodeSpec;
    case 'Line':
      return { ...common, type: 'line', width: (p.length as number) ?? (w as number) ?? 100, stroke: color(p.stroke) ?? '#000000' } as NodeSpec;
    case 'SVG':
      return { ...common, type: 'image', src: `data:image/svg+xml;utf8,${encodeURIComponent(String(p.src ?? ''))}`, width: (w as number) ?? 24, height: (hgt as number) ?? 24, fit: 'fit' } as NodeSpec;
    case 'Input': {
      const value = String(p.value ?? '');
      const ts = (p.inputFrameProps ?? {}) as Record<string, unknown>;
      return {
        ...common, type: 'frame', layout: 'row', padding: pad(ts.padding) ?? [6, 8, 6, 8], fill: color(ts.fill) ?? '#ffffff', stroke: color(ts.stroke) ?? '#d9d9d9', strokeWidth: 1, radius: (ts.cornerRadius as number) ?? 6,
        width: typeof w === 'number' ? w : w === 'fill' ? 'fill' : 200, height: 'hug',
        children: [{ type: 'text', ref: el.path + '.t', text: value || String(p.placeholder ?? ''), fontSize: (p.fontSize as number) ?? 14, fill: value ? (color(p.fill) ?? '#1e1e1e') : '#a0a0a0', width: 'fill' } as NodeSpec],
      } as NodeSpec;
    }
    case 'Image':
      return { ...common, type: 'image', src: String(p.src ?? ''), width: (w as number) ?? 100, height: (hgt as number) ?? 100 } as NodeSpec;
    default:
      return { ...common, type: 'frame', width: 1, height: 1 } as NodeSpec;
  }
}


export interface WidgetRender {
  tree: WidgetElement;
  state: Record<string, unknown>;
  menu: { propertyName: string; tooltip: string }[] | null;
}

/** Replace a widget's layers with a fresh render and store its state (one undoable step). */
export function applyWidgetRender(ed: Editor, widgetId: NodeId, out: WidgetRender) {
  const fresh = ed.doc.get(widgetId);
  if (!fresh?.widget) return;
  const root = widgetToSpec(out.tree);
  ed.transact(`Widget: ${fresh.widget.name}`, () => {
    ed.remove(ed.doc.children(widgetId).map((c) => c.id));
    // the widget frame takes the root element's look; its children are the rendered tree
    const { children, type: _t, ref: _r, name: _n, ...look } = root as NodeSpec & { children?: NodeSpec[] };
    void _t;
    void _r;
    void _n;
    const kids = root.type === 'frame' ? (children ?? []) : [root];
    const res = createFromSpec(ed, kids, widgetId);
    const rootProps = specToProps({ type: 'frame', ...(root.type === 'frame' ? look : { layout: 'row', width: 'hug', height: 'hug' }) } as NodeSpec, 'FRAME') as Record<string, unknown>;
    const keep: Record<string, unknown> = {};
    for (const k of ['fills', 'strokes', 'strokeWeight', 'cornerRadius', 'effects', 'layoutMode', 'itemSpacing', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'primaryAxisAlign', 'counterAxisAlign', 'layoutSizingH', 'layoutSizingV', 'width', 'height']) if (k in rootProps) keep[k] = rootProps[k];
    if (!('fills' in keep)) keep.fills = [];
    ed.update(widgetId, { ...keep, widget: { ...fresh.widget, state: out.state, menu: out.menu ?? undefined } } as never, 'Widget');
    // remember which rendered layer is which element (for clicks)
    const clickable = new Set<string>();
    const inputs = new Map<string, string>();
    const walk = (el: WidgetElement) => {
      if (el.clickable) clickable.add(el.path);
      if (el.input) inputs.set(el.path, String(el.props.value ?? ''));
      for (const c of el.children) if (typeof c !== 'string') walk(c);
    };
    walk(out.tree);
    if (out.tree.clickable) ed.update(widgetId, { pluginData: { widgetPath: out.tree.path, widgetClick: '1' } });
    for (const [path, id] of Object.entries(res.refs)) ed.update(id, { pluginData: { widgetPath: path, ...(clickable.has(path) ? { widgetClick: '1' } : {}), ...(inputs.has(path) ? { widgetInput: '1', widgetValue: inputs.get(path)! } : {}) }, locked: true });
  });
}

